import { Button, H2, H6 } from "@sellar-io/components";
import { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useMatch, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../../../components";
import { Alert } from "../../../../../components/alert/Alert";
import { InputLabel } from "@sellar-io/components";
import { NEW_VENUE } from "../../../../../constants/routes";
import { useSellarUser } from "../../../../../hooks";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useBusinessClaim } from "../../../../../hooks/api/v2/business/useBusinessClaim";
import { useMatchedBuyersList } from "../../../../../hooks/api/v2/retailer/useMatchedBuyersList";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../hooks/useSignupContext.shared";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "./BuyerCreationFlowStore";
import {
  BusinessClaimConfirmation,
  TermsAndConditionsConfirmation,
} from "./BuyerSignupFormStep";
import { useCreateTwoCompanyCreditLimitQuote } from "../../../../../hooks/api/v2/two/useCreateTwoCompanyCreditLimitQuote";

export const BuyerSignupBusinessMatchStep = () => {
  const creditLimitQuoteMutation = useCreateTwoCompanyCreditLimitQuote();
  const {
    setStep: setSignupStep,
    signupDialogActive,
    endOnboarding,
    setRedirectToMarket,
    hasAcceptedTermsAndConditions,
    setHasAcceptedTermsAndConditions,
    company,
    setCreditLimitQuoteValue,
    basketTotal,
    setBuyerSignUpBusinessId,
  } = useSignupContext();

  const {
    setPostcode,
    setStep,
    setBuyerDetails,
    buyerDetails,
    postcode,
    reset,
  } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      setStep: state.setStep,
      setBuyerDetails: state.setBuyerDetails,
      buyerDetails: state.buyerDetails,
      postcode: state.postcode,
      reset: state.reset,
      setPostcode: state.setPostcode,
    })),
  );
  const [searchParams] = useSearchParams();
  const { changeBusiness } = useSellarUser();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );

  const [confirmedAssociation, setConfirmedAssociation] = useState(false);

  const storefrontLinkPassword =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const businessUserInviteJwt = searchParams.get("jwt");
  const utmMedium = searchParams.get("utm_medium");
  const isNewVenue = !!useMatch(NEW_VENUE);
  const claimBusinessMutation = useBusinessClaim();
  const matchedBusinessesQuery = useMatchedBuyersList(
    {
      query: [
        {
          type: "postalCode",
          value: postcode ?? "",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!postcode,
    },
  );

  useEffect(() => {
    if (!postcode) {
      if (unAuthorisedUserPostcode) {
        return setPostcode(unAuthorisedUserPostcode);
      }
      return setStep(BuyerCreationFlowStep.BuyerPostcodeStep);
    }
  }, [unAuthorisedUserPostcode, postcode, setPostcode, setStep]);

  const claimBusiness = async (businessId: number | undefined) => {
    if (!businessId)
      throw new Error("Cannot claim a business that hasn't been created yet.");
    claimBusinessMutation.mutate(
      {
        data: {
          id: businessId,
          hasAcceptedTermsAndConditions,
        },
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
        },
      },
      {
        onSuccess: async (business) => {
          // store created business Id

          setBuyerSignUpBusinessId(business.id);

          TagManager.dataLayer({
            dataLayer: {
              event: "venue_claimed",
              businessId,
            },
          });

          // if creating venue in dashboard, return early and no redirect

          if (isNewVenue) {
            changeBusiness(business.id);
            return reset();
          }

          // run credit check

          const creditCheckQuote =
            company?.companyNumber && buyerDetails
              ? await creditLimitQuoteMutation.mutateAsync({
                  twoOrganisationNumber: company.companyNumber,
                  retailerId: buyerDetails.id,
                })
              : null;

          setCreditLimitQuoteValue(creditCheckQuote?.totalLimit ?? 0);

          // used to indicate if user has as draft basket and is signing up via a storefront page

          const hasBasketTotal = !!(basketTotal || basketTotal === 0);

          // if credit check value and no draft basket, navigate to CreditEligibleStep

          if (
            creditCheckQuote?.status === "eligible" &&
            creditCheckQuote.totalLimit > 0 &&
            !hasBasketTotal
          ) {
            return setSignupStep(SignupFlowStep.CreditEligibleStep);
          }

          // if sign up dialog active, show complete onboarding step (handles draft basket)

          if (signupDialogActive) {
            changeBusiness(business.id);
            return setSignupStep(SignupFlowStep.ConfirmationStep);
          }

          // if direct sign up, complete onboarding

          changeBusiness(business.id);
          setRedirectToMarket(true);
          endOnboarding();
        },
        onError: (error) => {
          const errorMessage = apiV2AssertError(error)?.response?.data.message;
          if (errorMessage === "Non-matching domain")
            return setStep(BuyerCreationFlowStep.BuyerClaimMismatch);
        },
      },
    );
  };
  if (matchedBusinessesQuery.isPending || claimBusinessMutation.isPending)
    return <SpinnerLegacy />;

  const canContinue =
    buyerDetails && confirmedAssociation && hasAcceptedTermsAndConditions;

  return (
    <div className={"flex h-full flex-col justify-center"}>
      {matchedBusinessesQuery.data?.pages[0]?.[0] ? (
        <div className={"flex flex-col gap-4"}>
          <div className={"flex flex-col gap-2"}>
            <H2 className={"text-center"}>Your delivery address</H2>
            <p className={"text-center"}>
              If you have multiple sites, you can add them later
            </p>
          </div>

          {claimBusinessMutation.isError ? (
            <Alert
              type={"error"}
              className={"w-full"}
              message={
                apiV2AssertError(claimBusinessMutation.error)?.response?.data
                  .message ?? "Unknown error"
              }
            />
          ) : null}
          <div className="flex w-full flex-col gap-2">
            <InputLabel>
              Please select your business at{" "}
              <span
                className={
                  "inline-flex cursor-pointer items-center whitespace-nowrap"
                }
                onClick={() => setStep(BuyerCreationFlowStep.BuyerPostcodeStep)}
              >
                <span className={"underline"}>{postcode}</span>{" "}
                <Icon className={"ml-1"} icon={"PENCIL"} />
              </span>
            </InputLabel>
            <div
              className={
                "max-h-[165px] divide-y overflow-scroll rounded-lg border border-solid"
              }
            >
              {matchedBusinessesQuery.data.pages.map((page) =>
                page.map((buyer) => (
                  <div
                    key={buyer.id}
                    className={twMerge(
                      "flex cursor-pointer flex-row items-center gap-3 p-4",
                      buyerDetails?.id &&
                        buyerDetails.id === buyer.id &&
                        "bg-gray-100",
                    )}
                    onClick={() =>
                      // an existing buyer on Sellar found
                      setBuyerDetails({
                        ...buyer,
                        SupplierRetailerConnection: null,
                        lastOrderedAt: null,
                        SupplierRetailerConnectionCount: null,
                      })
                    }
                  >
                    <>
                      {buyerDetails?.id && buyerDetails.id === buyer.id ? (
                        <Icon
                          icon={"CHECK_CIRCLE"}
                          className={
                            "h-[38px] max-h-[38px] min-h-[38px] w-[38px] max-w-[38px] min-w-[38px]"
                          }
                        />
                      ) : (
                        <ProfilePicture
                          name={buyer.Business.name}
                          logoUrl={buyer.Business.logoUrl ?? ""}
                        />
                      )}
                      <div className="flex flex-col">
                        <strong>{buyer.Business.name}</strong>
                        <H6>{buyer.Business.DeliveryAddress.postalCode}</H6>
                      </div>
                    </>
                  </div>
                )),
              )}
            </div>
            <H6
              className={"cursor-pointer underline"}
              onClick={() =>
                setStep(BuyerCreationFlowStep.BuyerBusinessSearchStep)
              }
            >
              Can't see your business?
            </H6>
          </div>
          <div className={"col-span-2 mt-auto flex flex-col gap-2"}>
            {!!buyerDetails && (
              <>
                <BusinessClaimConfirmation
                  onChange={(value) => setConfirmedAssociation(value)}
                />
                <TermsAndConditionsConfirmation
                  onChange={(value) => setHasAcceptedTermsAndConditions(value)}
                />
              </>
            )}
            <Button
              disabled={!canContinue}
              onClick={() => {
                // a buyer that exists on the platform has existing business users
                if (
                  buyerDetails &&
                  !!buyerDetails.Business.BusinessUsers.length
                ) {
                  return setStep(BuyerCreationFlowStep.BuyerBusinessClaimed);
                }

                // buyer exists on the platform without business users
                return claimBusiness(buyerDetails?.Business.id);
              }}
              className={"w-full"}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className={"flex h-full flex-col justify-center gap-4"}>
          <H2 className={"my-4 text-center"}>
            Finish setting up your delivery address at{" "}
            <span
              className={
                "inline-flex cursor-pointer items-center whitespace-nowrap"
              }
              onClick={() => setStep(BuyerCreationFlowStep.BuyerPostcodeStep)}
            >
              <span className={"underline"}>{postcode}</span>
              <Icon className={"ml-1"} icon={"PENCIL"} />
            </span>
          </H2>
          <Button
            onClick={() => {
              setStep(BuyerCreationFlowStep.BuyerBusinessSearchStep);
            }}
            className={"w-full"}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
};
